import type { ReactNode } from 'react';
import { cn } from '@reflex/ui/lib/utils';
import type { RunloopAccountSummary } from '@/lib/org-setup-api';

interface RunloopConnectedAccountProps {
  /**
   * Live `{ id, name, tier }` account behind the org's saved
   * `RUNLOOP_API_KEY`, read from `GET .../sandbox-provider`.
   */
  account: RunloopAccountSummary | null;
  /** Rendered when `account` is null (e.g. onboarding paid-tier note). */
  fallback?: ReactNode;
  /** `inline` for IntegrationSecretGroup descriptions; `block` for dialogs. */
  variant?: 'inline' | 'block';
  className?: string;
}

/**
 * Shared visualization of the Runloop account a saved `RUNLOOP_API_KEY`
 * resolves to. Used by the org-setup wizard and Admin → Secrets.
 */
export function RunloopConnectedAccount({
  account,
  fallback,
  variant = 'inline',
  className,
}: RunloopConnectedAccountProps) {
  const content: ReactNode = account ? (
    <span data-testid="runloop-account-summary">
      Connected to <strong className="font-medium text-foreground">{account.name}</strong> (
      {account.id}, {account.tierLabel} tier)
    </span>
  ) : (
    (fallback ?? null)
  );

  if (content == null) return null;

  if (variant === 'block') {
    return (
      <div
        className={cn(
          'rounded-md border bg-muted/30 px-3 py-2 text-xs leading-snug text-muted-foreground',
          className,
        )}
      >
        {content}
      </div>
    );
  }
  return <span className={className}>{content}</span>;
}
